import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, ArrowRight, XCircle } from "lucide-react";
import { brand } from "../mock";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_POLLS = 8;

export const PaymentSuccess = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking"); // checking | paid | timeout | error
  const [record, setRecord] = useState(null);
  const pollCount = useRef(0);

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      try {
        const r = await axios.get(`${API}/payments/status/${sessionId}`);
        if (cancelled) return;
        setRecord(r.data);
        if (r.data.payment_status === "paid") {
          setStatus("paid");
          return;
        }
        if (["failed", "expired"].includes(r.data.payment_status)) {
          setStatus("error");
          return;
        }
        if (++pollCount.current >= MAX_POLLS) {
          setStatus("timeout");
          return;
        }
        setTimeout(poll, 2000);
      } catch (err) {
        if (cancelled) return;
        if (++pollCount.current >= MAX_POLLS) { setStatus("error"); return; }
        setTimeout(poll, 2500);
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-[#0A0714] text-[#EDE3D0] relative overflow-hidden">
      <div className="pointer-events-none absolute -top-40 -left-24 w-[520px] h-[520px] rounded-full bg-[#A36CFF]/20 blur-[140px]" />
      <div className="pointer-events-none absolute -bottom-40 -right-24 w-[560px] h-[560px] rounded-full bg-[#D4A85B]/15 blur-[140px]" />
      <div className="relative max-w-2xl mx-auto px-6 md:px-10 py-24">
        {status === "checking" && (
          <div className="text-center" data-testid="payment-checking">
            <Loader2 className="mx-auto text-[#D4A85B] animate-spin" size={48} />
            <h1 className="font-serif text-3xl md:text-4xl mt-8 text-[#EDE3D0]">
              Just a breath — <span className="italic text-[#D4A85B]">confirming your payment.</span>
            </h1>
            <p className="mt-4 text-[#EDE3D0]/70">This usually takes 2–5 seconds.</p>
          </div>
        )}

        {status === "paid" && (
          <div className="text-center" data-testid="payment-success">
            <div className="w-20 h-20 rounded-full bg-[#D4A85B]/15 border border-[#D4A85B] flex items-center justify-center text-[#D4A85B] mx-auto">
              <CheckCircle2 size={36} />
            </div>
            <div className="mt-6 text-[10px] uppercase tracking-[0.32em] text-[#D4A85B]">Payment received</div>
            <h1 className="font-serif text-4xl md:text-5xl leading-[1.1] mt-4 text-[#EDE3D0]">
              You're <span className="italic text-[#D4A85B]">booked.</span>
            </h1>
            <p className="mt-6 text-[#EDE3D0]/75 max-w-lg mx-auto leading-relaxed">
              A confirmation with your Calendly link has been sent to your email. Choose a time that works for you and I'll see you soon.
            </p>
            {record?.amount && (
              <p className="mt-3 text-xs text-[#EDE3D0]/60">
                Charged: ${(record.amount / 100).toFixed(2)} {String(record.currency || "usd").toUpperCase()}
              </p>
            )}
            <div className="mt-10 flex flex-col md:flex-row items-center justify-center gap-3">
              <a
                href={brand.calendlyUrl}
                target="_blank"
                rel="noreferrer"
                data-testid="post-payment-calendly"
                className="inline-flex items-center gap-2 rounded-full bg-[#D4A85B] text-[#0A0714] px-7 py-3.5 text-xs uppercase tracking-[0.28em] hover:bg-[#E9C77B] transition"
              >
                Choose your session time <ArrowRight size={14} />
              </a>
              <Link
                to="/"
                className="inline-flex items-center gap-2 rounded-full border border-[#D4A85B]/50 text-[#EDE3D0] px-6 py-3 text-xs uppercase tracking-[0.28em] hover:border-[#D4A85B] hover:text-[#D4A85B] transition"
              >
                Back to site
              </Link>
            </div>
          </div>
        )}

        {(status === "timeout" || status === "error") && (
          <div className="text-center" data-testid="payment-issue">
            <div className="w-20 h-20 rounded-full bg-white/5 border border-[#D4A85B]/40 flex items-center justify-center text-[#D4A85B] mx-auto">
              <XCircle size={36} />
            </div>
            <h1 className="font-serif text-3xl md:text-4xl mt-8 text-[#EDE3D0]">
              {status === "timeout" ? "Taking a moment longer than usual." : "Something didn't complete."}
            </h1>
            <p className="mt-4 text-[#EDE3D0]/70 max-w-lg mx-auto">
              {status === "timeout"
                ? "Your card may still be processing. Please refresh this page in a minute — or check your email for a receipt."
                : `Please try again, or reach out at ${brand.email} and we'll sort it together.`}
            </p>
            <Link
              to="/"
              className="mt-8 inline-flex items-center gap-2 rounded-full border border-[#D4A85B]/50 text-[#EDE3D0] px-6 py-3 text-xs uppercase tracking-[0.28em] hover:border-[#D4A85B] hover:text-[#D4A85B] transition"
            >
              Back to site
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export const PaymentCancel = () => {
  return (
    <div className="min-h-screen bg-[#0A0714] text-[#EDE3D0] flex items-center justify-center p-6">
      <div className="text-center max-w-xl" data-testid="payment-cancel">
        <div className="text-[10px] uppercase tracking-[0.32em] text-[#D4A85B]">Payment canceled</div>
        <h1 className="font-serif text-4xl md:text-5xl leading-[1.1] mt-4 text-[#EDE3D0]">
          Your seat is still <span className="italic text-[#D4A85B]">held</span> — no rush.
        </h1>
        <p className="mt-6 text-[#EDE3D0]/75 leading-relaxed">
          Your intake was received. Whenever you're ready to finish booking, come back to the services page and pick your session again.
        </p>
        <Link
          to="/#services"
          className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#D4A85B] text-[#0A0714] px-7 py-3.5 text-xs uppercase tracking-[0.28em] hover:bg-[#E9C77B] transition"
        >
          Return to services <ArrowRight size={14} />
        </Link>
      </div>
    </div>
  );
};
